import axios, { AxiosError, InternalAxiosRequestConfig } from "axios"
import localStorage from "@/utils/localStorage"
import { clearAdminAuthStorage, redirectToAdminLoginAfterAuthFailure } from "@/utils/admin-auth-client"

const internal = axios.create({
  baseURL: process.env.APP_URL
})

const rawClient = axios.create({
  baseURL: process.env.APP_URL
})

let refreshPromise: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  const rt = localStorage.getItem("_rt")
  if (!rt) return null
  try {
    const { data } = await rawClient.post("/api/admin/refresh", { refreshToken: rt })
    if (data?.accessToken) localStorage.setItem("_nt", data.accessToken)
    if (data?.refreshToken) localStorage.setItem("_rt", data.refreshToken)
    return data?.accessToken ?? null
  } catch {
    return null
  }
}

type ConfigWithRetry = InternalAxiosRequestConfig & { __retryAfterRefresh?: boolean }

internal.interceptors.request.use(
  function (config) {
    const token = localStorage.getItem("_nt")

    if (!!token) {
      config.headers["Authorization"] = `${token}`
    }
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

internal.interceptors.response.use(
  function (response) {
    return response
  },
  async function (error: AxiosError) {
    const config = error.config as ConfigWithRetry | undefined
    if (!config || config.__retryAfterRefresh) {
      return Promise.reject(error)
    }
    if (error.response?.status !== 401) {
      return Promise.reject(error)
    }
    if (typeof window === "undefined") {
      return Promise.reject(error)
    }
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null
      })
    }
    const newToken = await refreshPromise
    if (!newToken) {
      clearAdminAuthStorage()
      redirectToAdminLoginAfterAuthFailure()
      return Promise.reject(error)
    }
    config.__retryAfterRefresh = true
    config.headers = config.headers ?? {}
    config.headers["Authorization"] = newToken
    return internal.request(config)
  }
)

export default internal
